import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { EuiFlexItem, EuiLink, EuiButton, EuiFlexGroup } from '@elastic/eui';

import Page from 'components/Page';
import Table from 'components/Table';
import ContactDetails from 'modules/_global/ContactDetails';
import { getPatients } from './patients.fetch';
import { addToast } from '../dashboard/dashboard.actions';
import AddPatient from './AddPatient';

const Users = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [patients, setUsers] = useState([]);
	const [selection, setSelection] = useState([]);
	const [error, setError] = useState(null);
	// eslint-disable-next-line no-unused-vars
	const [isLoading, setIsLoading] = useState(false);
	const [addPatientVisisble, setAddPatientVisible] = useState(false);

	useEffect(() => {
		async function fetchPatients() {
			try {
				setIsLoading(true);
				const results = await getPatients();
				setUsers(results.data);
			} catch (err) {
				setError(err.message || 'Something went wrong');
			} finally {
				setError(null);
				setIsLoading(false);
			}
		}
		fetchPatients();
	}, []);

	useEffect(() => {
		function toastError() {
			dispatch(addToast('Error', error, 'danger', 'help'));
			setTimeout(() => {
				setError(null);
			}, 2000);
		}
		if (error) toastError();
	}, [error]);

	const selectionProps = {
		selectable: () => true,
		onSelectionChange: (select) => {
			setSelection(select);
		},
		selectableMessage: () => undefined,
		initialSelected: selection,
	};

	const addPatient = async (data) => {
		try {
			// eslint-disable-next-line no-unused-vars
			let payload = {
				userId: data.userId,
				firstName: data.firstName,
				middleName: data.middleName,
				lastName: data.lastName,
				addressLine1: data.addressLine1,
				addressLine2: data.addressLine2,
				postal: data.postal,
				role: data.role,
			};

			if (data.role === 'pharmacist') {
				payload = {
					...payload,
					prcLincense: data.prcLincense,
				};
			}
		} catch (err) {
			setError(err.message || 'Something went wrong');
		} finally {
			setError(null);
			setIsLoading(false);
			setAddPatientVisible(false);
		}
	};

	const clearFilters = async () => {
		try {
			setIsLoading(true);
		} catch (err) {
			setError(err.message || 'Something went wrong');
		} finally {
			setError(null);
			setIsLoading(false);
		}
	};

	const renderToolsLeft = () => {
		if (!selection.length) {
			return null;
		}

		return (
			<EuiButton color="danger" fill={false} iconType="trash">
				Delete
			</EuiButton>
		);
	};

	const renderToolsRight = () => [
		<EuiFlexItem key="clearFilter" grow={false} justifyContent="center">
			<EuiButton color="disabled" iconType="broom" onClick={clearFilters}>
				Clear Filters
			</EuiButton>
		</EuiFlexItem>,
	];

	const search = {
		box: {
			incremental: true,
		},
		toolsLeft: renderToolsLeft(),
		toolsRight: renderToolsRight(),
		filters: [],
	};

	// const action = () => {};

	const columns = [
		{
			field: 'id',
			name: 'Name',
			sortable: true,
			render: (id, patient) => (
				<Link to={`/patients/${id}`}>
					<EuiLink>
						{`
							${patient.lastName}, 
							${patient.firstName} 
							${patient.middleName || ''}
						`}
					</EuiLink>
				</Link>
			),
		},
		{
			field: 'contactDetails',
			name: 'Patient Contact',
			sortable: true,
			render: (ids) => <ContactDetails ids={ids} />,
		},
		// {
		// 	actions: [
		// 		{
		// 			name: 'View',
		// 			description: 'View',
		// 			onClick: async (data) => action(data, 'view'),
		// 			icon: 'eye',
		// 			type: 'icon',
		// 			isPrimary: true,
		// 		},
		// 		{
		// 			name: 'Edit',
		// 			description: 'Edit',
		// 			onClick: (data) => action(data, 'edit'),
		// 			icon: 'pencil',
		// 			type: 'icon',
		// 			color: 'primary',
		// 			isPrimary: true,
		// 		},
		// 		{
		// 			name: 'Delete',
		// 			description: 'Delete',
		// 			onClick: (data) => action(data, 'delete'),
		// 			icon: 'trash',
		// 			type: 'icon',
		// 			color: 'blue',
		// 			isPrimary: true,
		// 		},
		// 	],
		// },
	];

	return (
		<>
			<Helmet title={t('patients.title')} />
			<Page
				headerRight={
					<EuiFlexGroup direction="row" gutterSize="m">
						<EuiFlexItem>
							<EuiButton
								color="primary"
								disabled
								fill
								iconType="plusInCircleFilled"
								onClick={() => setAddPatientVisible(true)}
							>
								Add Patient
							</EuiButton>
						</EuiFlexItem>
					</EuiFlexGroup>
				}
				title={t('patients.title')}
			>
				<Table
					columns={columns}
					isSelectable
					itemId="userId"
					items={patients}
					search={search}
					selection={selectionProps}
				/>

				<AddPatient
					addPatient={addPatient}
					onClose={() => setAddPatientVisible(false)}
					setError={setError}
					visible={addPatientVisisble}
				/>
			</Page>
		</>
	);
};

export default Users;
